'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher, MobileLanguageSwitcher } from './language-switcher';
import { DesktopNav } from './desktop-nav';
import { LinkItem } from './nav-link-item';
import { productLinks, companyLinks, companyLinks2 } from './nav-links';
import { cn } from '@/lib/utils';
import { signUpUrl } from '@/lib/links';
import { Logo } from '@/components/logo';

// Per-row tap/hover surface for the full-screen mobile menu.
const mobileItem = 'p-2 transition-brand hover:bg-accent active:bg-accent';
const sectionLabel = 'px-2 text-small font-medium text-muted-foreground';
const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Concave fillet that visually grafts the floating bar onto the green page frame:
// one sits at each top corner, filled with the frame colour so the bar reads as
// "carved out of" the frame rather than floating over the cream panel.
const CORNER_PATH = 'M5.50871e-06 0C-0.00788227 37.3001 8.99616 50.0116 50 50H5.50871e-06V0Z';

export function Header() {
  const t = useTranslations('header');
  const tMenu = useTranslations('header.menu');
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolledHidden, setScrolledHidden] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Close whenever the route changes (covers browser back/forward).
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Hide the bar when scrolling down, reveal it when scrolling up.
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      setScrolledHidden(y > lastY && y > 140);
      lastY = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // While open: lock background scroll and keep keyboard focus inside the modal menu.
  useEffect(() => {
    if (!mobileOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Pause Lenis too — it captures wheel/touch gestures at the window level
    // and was routing them to the (locked) page, so the menu's own
    // overflow-y-auto never received them and the panel couldn't scroll.
    const lenis = (window as unknown as { lenis?: { stop: () => void; start: () => void } }).lenis;
    lenis?.stop();
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(focusableSelector),
      );
      if (focusable.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (
        e.shiftKey &&
        (active === first || active === panelRef.current || !panelRef.current.contains(active))
      ) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      lenis?.start();
      document.removeEventListener('keydown', onKeyDown);
      toggleRef.current?.focus();
    };
  }, [mobileOpen]);

  return (
    <>
      {/* Frame-coloured bar: a centred floating tab on desktop that grafts onto
          the palm-green page frame (rounded bottom + corner fillets); full-width
          and edge-anchored under 850px. */}
      <header
        className={cn(
          'fixed start-1/2 top-1.5 z-50 w-full max-w-5xl -translate-x-1/2 rounded-b-[1.45rem] bg-primary text-primary-foreground shadow-[0_18px_44px_-18px_rgba(15,61,46,0.45)] transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] rtl:translate-x-1/2 sm:top-2',
          'max-[1200px]:max-w-2xl',
          // Mobile: a floating fully-rounded pill (no frame graft, no fillets) — but
          // when the full-screen menu is open it spans edge-to-edge like the sheet.
          'max-[850px]:top-2 max-[850px]:w-auto max-[850px]:max-w-none max-[850px]:translate-x-0 max-[850px]:rounded-full rtl:max-[850px]:translate-x-0',
          mobileOpen
            ? 'max-[850px]:start-1.5 max-[850px]:end-1.5'
            : 'max-[850px]:start-3 max-[850px]:end-3',
          // Open menu: the bar turns the same cream as the full-screen menu so the
          // two read as one continuous sheet (logo + toggle go dark to match).
          mobileOpen && 'bg-background text-foreground shadow-none',
          scrolledHidden && !mobileOpen && '-translate-y-[160%]',
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 max-[850px]:h-14 max-[850px]:px-6">
          <Link
            href="/"
            onClick={closeMobile}
            className="group flex shrink-0 items-center ms-4 max-[850px]:ms-0"
          >
            <Logo
              variant={mobileOpen ? 'default' : 'light'}
              className="h-7 transition-brand group-hover:opacity-85 sm:h-8"
            />
          </Link>

          {/* Product / Company expandable menus (≥850px) */}
          <div className="max-[850px]:hidden">
            <DesktopNav />
          </div>

          {/* Right cluster (≥850px): language + the split "try for free" CTA. */}
          <div className="flex items-center gap-4 max-[850px]:hidden">
            <LanguageSwitcher className="text-primary-foreground/75 hover:text-primary-foreground" />
            <Button asChild size="sm" variant="secondary" className="h-9 shrink-0 px-5">
              <a href={signUpUrl}>{t('cta')}</a>
            </Button>
          </div>

          {/* Toggle (<850px) */}
          <Button
            ref={toggleRef}
            type="button"
            variant="ghost"
            size="icon"
            data-brand-gesture="quiet"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? tMenu('closeMenu') : tMenu('openMenu')}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            className={cn(
              'relative hidden size-10 shrink-0 place-items-center max-[850px]:grid',
              mobileOpen ? 'text-foreground' : 'text-primary-foreground',
            )}
          >
            <Menu
              aria-hidden
              className={cn(
                'col-start-1 row-start-1 size-5 transition-all duration-200 ease-[cubic-bezier(0.65,0,0.35,1)]',
                mobileOpen ? 'scale-0 -rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100',
              )}
            />
            <X
              aria-hidden
              className={cn(
                'col-start-1 row-start-1 size-5 transition-all duration-200 ease-[cubic-bezier(0.65,0,0.35,1)]',
                mobileOpen ? 'scale-100 rotate-0 opacity-100' : 'scale-0 rotate-90 opacity-0',
              )}
            />
          </Button>
        </div>

        {/* Corner fillets — graft the bar's top edge onto the page frame. Hidden while
            the mobile menu is open (the bar goes edge-to-edge then). */}
        {/* The start/end offsets mirror on their own in RTL; only the rotation
            needs swapping so the concave curve keeps facing the bar on whichever
            physical side the fillet lands. */}
        <svg
          aria-hidden
          className={cn(
            'pointer-events-none absolute -start-[3.0625rem] top-0 rotate-180 text-primary rtl:rotate-90',
            'max-[850px]:hidden',
            mobileOpen && 'hidden',
          )}
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <path d={CORNER_PATH} fill="currentColor" />
        </svg>
        <svg
          aria-hidden
          className={cn(
            'pointer-events-none absolute -end-[3.0625rem] top-0 rotate-90 text-primary rtl:rotate-180',
            'max-[850px]:hidden',
            mobileOpen && 'hidden',
          )}
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <path d={CORNER_PATH} fill="currentColor" />
        </svg>
      </header>

      {/* Full-screen mobile menu — solid sand ground; sits below the bar. */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={tMenu('menuLabel')}
          tabIndex={-1}
          className="fixed inset-0 z-40 bg-background outline-none max-[850px]:block min-[851px]:hidden"
        >
          {/* data-lenis-prevent: gestures inside the panel scroll the panel,
              never the smooth-scrolled page behind it. */}
          <div
            data-lenis-prevent
            className="h-full overflow-y-auto overscroll-contain px-5 pt-32 pb-10 animate-in fade-in-0 zoom-in-95 duration-200 ease-out"
          >
            <nav className="flex w-full flex-col">
              <div className="flex w-full flex-col gap-y-2">
                <span className={sectionLabel}>{tMenu('product')}</span>
                {productLinks.map((item) => (
                  <LinkItem
                    key={item.key}
                    href={item.href}
                    icon={item.icon}
                    external={item.external}
                    label={tMenu(`productLinks.${item.key}.label`)}
                    description={tMenu(`productLinks.${item.key}.description`)}
                    onClick={closeMobile}
                    className={mobileItem}
                  />
                ))}

                <span className={cn(sectionLabel, 'mt-2')}>{tMenu('company')}</span>
                {companyLinks.map((item) => (
                  <LinkItem
                    key={item.key}
                    href={item.href}
                    icon={item.icon}
                    external={item.external}
                    label={tMenu(`companyLinks.${item.key}.label`)}
                    description={tMenu(`companyLinks.${item.key}.description`)}
                    onClick={closeMobile}
                    className={mobileItem}
                  />
                ))}
                {companyLinks2.map((item) => (
                  <LinkItem
                    key={item.key}
                    href={item.href}
                    icon={item.icon}
                    external={item.external}
                    label={tMenu(`companyLinks2.${item.key}`)}
                    onClick={closeMobile}
                    className={mobileItem}
                  />
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <MobileLanguageSwitcher />
                <Button asChild size="lg" className="w-full">
                  <a href={signUpUrl} onClick={closeMobile}>
                    {t('cta')}
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
